import './ButtonsMixin.scss';
import './ButtonsMixin.m.scss';
import DIContainer, { getInstance } from '@components/DependencyInjectionContainer.js';
import { Observable } from 'rxjs';
import { createElement, debounce } from '@components/Utility/index.js';
export function ButtonsMixin(classToExtend) {
    return class extends classToExtend {
        constructor() {
            super(...arguments);
            this.buttonContainers = new Map();
            this._buttonClickAwayEventId = '';
            this.debouncedButtonClick = debounce((buttonId, event) => {
                this.buttonClick(buttonId, event);
            }, 50);
            this.buttonContainerHandleResize = debounce((id /*element: ResizeObserverEntry*/) => {
                const buttonContainer = this.buttonContainerGet(id);
                const containerWidth = buttonContainer.clientWidth - 10;
                if (containerWidth === -10) {
                    return; // container is presumably not visible so just dont waste any processing on it. 
                }
                if (buttonContainer.classList.contains('overflowtype-scroll')) {
                    const internalButtonContainer = buttonContainer.querySelector('.button-scroll-container');
                    const overflowButton = this.buttonGet(`${id}_overflow_scroll-l`);
                    const overflowButton2 = this.buttonGet(`${id}_overflow_scroll-r`);
                    let scrollWidth = internalButtonContainer.scrollWidth;
                    if (!overflowButton.hidden) {
                        scrollWidth -= overflowButton.renderedWidth || 0;
                    }
                    if (!overflowButton2.hidden) {
                        scrollWidth -= overflowButton2.renderedWidth || 0;
                    }
                    const shouldHide = (internalButtonContainer.clientWidth - scrollWidth) > -10;
                    if (!!overflowButton.hidden !== shouldHide) {
                        this.buttonHiddenSet(`${id}_overflow_scroll-l`, shouldHide);
                    }
                    if (!!overflowButton2.hidden !== shouldHide) {
                        this.buttonHiddenSet(`${id}_overflow_scroll-r`, shouldHide);
                    }
                }
                else if (buttonContainer.classList.contains('overflowtype-menu')) {
                    const buttonElements = Array.from(buttonContainer.querySelectorAll(':scope > button:not(.hidden)'));
                    let buttonWidth = 0;
                    buttonElements.forEach((buttonElement) => {
                        const buttonId = buttonElement.getAttribute('data-button-id');
                        const button = this.buttonGet(buttonId);
                        buttonWidth += button.renderedWidth || 0;
                    });
                    let render = false;
                    if (buttonWidth > containerWidth) {
                        render = true;
                    }
                    else {
                        buttonWidth = this.buttonGet(`${id}_overflow_menu`)?.renderedWidth || 0;
                        let visibleButtons = buttonWidth ? 1 : 0;
                        this.buttons.forEach((button) => {
                            if (button.containerId === id && button.renderedWidth && button.id !== `${id}_overflow_menu`) {
                                buttonWidth += button.renderedWidth;
                                if (buttonWidth < containerWidth) {
                                    visibleButtons++;
                                }
                            }
                        });
                        if (visibleButtons !== buttonElements.length) {
                            render = true;
                        }
                    }
                    if (render) {
                        this.renderButtons(id);
                    }
                }
            }, 100);
        }
        get buttons() {
            if (!this._buttons) {
                this._buttons = [];
            }
            return this._buttons;
        }
        get IconManager() {
            if (!this.dependencies.IconManager) {
                // we didnt receive a IconManager dependency injection, so just use the global singleton
                this.dependencies.IconManager = getInstance('IconManager');
            }
            return this.dependencies.IconManager;
        }
        // due to timing issues, this needs to be manually called in each subclasses constructor that uses ButtonsMixin. 
        // constructor(...args: any[]) {
        // 	super(args[0], args[1], args[2]);
        // 	this.initializeButtons();
        // }
        // protected initializeButtons() {
        //	
        // }
        propertiesLoad(properties) {
            if (Reflect.has(properties, 'buttons')) {
                properties.buttons.forEach((button) => {
                    button.standardButton = !!this.buttonGet(button.id);
                    if (!button.index && !button.standardButton) {
                        button.index = -1; // ideally want to keep the custom buttons to the left side. 
                    }
                    this.buttonAdd(button);
                });
                delete properties.buttons;
                this.updateDisabledState();
            }
            super.propertiesLoad(properties);
        }
        renderHelpAnchor() {
            if (this.buttonGet('helpanchor')) {
                this.buttonRemove('helpanchor');
            }
            if (this.helpAnchor) {
                this.buttonAdd(DIContainer.getInstance('HelpAnchorManager').buttonConfigGet(this.helpAnchor, this));
            }
        }
        buttonAnimate(id, animate) {
            const button = this.buttonGet(id);
            if (!button) {
                return;
            }
            const btnElement = this.buttonContainerGet(button.containerId)?.querySelector(`button[data-button-id="${id}"]`);
            if (!btnElement) {
                button.animate = animate;
                return;
            }
            if (typeof animate === 'string')
                animate = [animate];
            if (button.animate) {
                if (typeof button.animate === 'string')
                    button.animate = [button.animate];
                // Remove any animate classes that are in button.animate and NOT in animate.
                const currentAnimations = button.animate;
                currentAnimations.forEach((animation) => {
                    if (!animate.includes(animation)) {
                        btnElement.classList.remove(`animate-${animation}`);
                    }
                });
            }
            animate.forEach((animation) => {
                if (!btnElement.classList.contains(`animate-${animation}`)) {
                    btnElement.classList.add(`animate-${animation}`);
                }
            });
            button.animate = animate;
        }
        buttonDefaultsSet(button) {
            button.containerId = button.containerId || '';
            if (typeof button.index === 'undefined') {
                button.index = this.buttons.length;
            }
            // prefer not to rely on the dynamic captionKey. if we had used it anywhere we can just hardcode those ones now.
            // button.captionKey = button.captionKey || (this.captionKey ? `${this.captionKey}_${button.id}` : undefined);
            /** Moved to render button to avoid possible cases where the button is added prior to the captions loading IE toolbar **/
            // if (button.captionKey) {
            // 	button.caption = this.CaptionManager.getCaption(`${button.captionKey}_caption`) || button.caption; // database caption trumps hardcoded captions
            // 	button.tooltip = this.CaptionManager.getCaption(`${button.captionKey}_tooltip`) || button.tooltip;
            // }
            if (this.formDesignerMode) {
                button.formBuilderDefaultValues = { ...button };
            }
            button.disabledState = this.shouldButtonDisable(button);
            return button;
        }
        buttonAdd(button) {
            if (this.buttonGet(button.id)) {
                const existingButton = this.buttonGet(button.id);
                console.log('id already exists');
                if (existingButton.eventConfigName && existingButton.eventConfigName !== button.eventConfigName) {
                    delete this.dynamicCodeEvents[existingButton.eventConfigName];
                }
                Object.assign(existingButton, button);
                // existingButton = { ...existingButton, ...button };
                button = existingButton;
            }
            else {
                button = this.buttonDefaultsSet(button);
                this.buttons.push(button);
            }
            if (!Reflect.has(button, 'standardButton')) {
                button.standardButton = true;
            }
            if (button.includeProperty && !Reflect.has(button, 'defaultIncludePropertyValue')) {
                button.defaultIncludePropertyValue = this.propertyGet(button.includeProperty);
            }
            if (button.eventConfigName) {
                this.dynamicCodeEvents[button.eventConfigName] = 'click';
            }
            if (button.captionKey && !this.CaptionManager.hasKey(button.captionKey)) {
                this.CaptionManager.onLoad$(button.captionKey, true).subscribe(() => {
                    if (this.drawn) {
                        this.renderButtons(button.containerId);
                    }
                });
            }
            if (this.drawn) {
                this.renderButtons(button.containerId);
            }
        }
        buttonRemove(id) {
            const button = this.buttonGet(id);
            if (button) {
                const containerId = button.containerId || '';
                this._buttons = this.buttons.filter(button => button.id !== id);
                this.renderButtons(containerId);
                if (button.eventConfigName) {
                    delete this.dynamicCodeEvents[button.eventConfigName];
                }
            }
        }
        buttonShow(id) {
            const button = this.buttonGet(id);
            if (button && button.hidden) {
                button.hidden = false;
                // TODO: we need a function to just refresh an existing button and not render the whole container again. 
                this.renderButtons(button.containerId);
                if (button.eventConfigName) {
                    this.dynamicCodeEvents[button.eventConfigName] = 'click';
                }
            }
        }
        buttonHide(id) {
            const button = this.buttonGet(id);
            if (button && !button.hidden) {
                button.hidden = true;
                this.renderButtons(button.containerId);
                if (button.eventConfigName) {
                    this.dynamicCodeEvents[button.eventConfigName] = '';
                }
            }
        }
        buttonHiddenSet(id, hidden) {
            if (hidden) {
                this.buttonHide(id);
            }
            else {
                this.buttonShow(id);
            }
        }
        buttonElementGet(buttonId) {
            if (!this.drawn) {
                return;
            }
            const button = this.buttonGet(buttonId);
            const containerId = button.containerId;
            let buttonElement = this.buttonContainerGet(containerId)?.querySelector(`button[data-button-id="${buttonId}"]`);
            if (!buttonElement) {
                buttonElement = document.body.querySelector(`.overflow-menu.${this.globalId}_${containerId} button[data-button-id="${buttonId}"]`);
            }
            return buttonElement;
        }
        buttonClick(buttonId, event) {
            const button = this.buttonGet(buttonId);
            let included = true;
            if (button && button.includeProperty) {
                included = this.propertyGet(button.includeProperty);
            }
            if (!button || button.disabledState || (button.hidden && !this.formDesignerMode) || !included) {
                return;
            }
            if (button.viewCondition && !DIContainer.getInstance('ConditionsManager').validateConditions(button.viewCondition, {
                initiator: this,
                record: this.record
            })) {
                return;
            }
            if (!event) {
                const buttonElement = this.buttonElementGet(buttonId);
                if (!buttonElement) {
                    return;
                }
                const rect = buttonElement.getBoundingClientRect();
                // synthetic event if event not provided
                event = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    clientX: rect.left + rect.width / 2,
                    clientY: rect.top + rect.height / 2 // Set clientY to the center of the buttonElement
                });
                buttonElement.dispatchEvent(event);
                return;
            }
            const overflowButton = event.target.closest('.overflow-menu-button');
            if (overflowButton && buttonId.indexOf('_overflow_menu') === -1) {
                const menu = overflowButton.lastChild;
                menu.style.display = 'none';
                setTimeout(() => {
                    if (menu) {
                        menu.style.display = '';
                    }
                }, 100);
            }
            if (!event) {
                // the component should already have focus by clicking the button in the first place, so we only will do this for a programmatic click
                // this was interfering with date components, causing buttons to open the picker
                this.focus();
            }
            if (!button.customEventConfigTrigger) {
                const eventName = button.eventConfigName || `${button.id}_click`;
                if (button.onClick) {
                    this.trigger(eventName, {
                        defaultEventHandler: () => {
                            if (button && button.onClick) {
                                button.onClick(event);
                            }
                        },
                        event: event
                    });
                }
                else {
                    this.trigger(eventName, {
                        event: event
                    });
                }
            }
            else if (button.onClick) {
                button.onClick(event);
            }
        }
        buttonDisable(id) {
            const button = this.buttonGet(id);
            if (button && !button.disabled) {
                button.disabled = true;
                button.disabledState = this.shouldButtonDisable(button);
                this.renderButtons(button.containerId);
            }
        }
        buttonEnable(id) {
            const button = this.buttonGet(id);
            if (button && button.disabled) {
                button.disabled = false;
                button.disabledState = this.shouldButtonDisable(button);
                this.renderButtons(button.containerId);
            }
        }
        buttonDisabledSet(id, disabled) {
            if (disabled) {
                this.buttonDisable(id);
            }
            else {
                this.buttonEnable(id);
            }
        }
        buttonGet(id) {
            return this.buttons.find(button => button.id === id);
        }
        buttonClassListUpdate(id, className, add) {
            const button = this.buttonGet(id);
            if (button) {
                button.classList = button.classList || [];
                if (add) {
                    if (!button.classList.includes(className)) {
                        button.classList.push(className);
                        this.refreshButton(id);
                    }
                }
                else {
                    // Remove the class if it exists.
                    const index = button.classList.indexOf(className);
                    if (index !== -1) {
                        button.classList.splice(index, 1);
                        this.refreshButton(id);
                    }
                }
            }
        }
        // visually show the button is active, ie the currently selected tab button. 
        buttonActiveSet(id, active = true, deactivateOthers = false) {
            const button = this.buttonGet(id);
            if (!button) {
                return;
            }
            // putting this first removes the need to filter the list
            if (deactivateOthers) {
                //const otherButtons = this.buttons.filter(item => item.id !== id);
                this.buttons.forEach((btn) => {
                    if (btn.containerId === button.containerId) {
                        this.buttonActiveSet(btn.id, false, false);
                    }
                });
            }
            button.active = active;
            const buttonElement = this.buttonElementGet(id);
            if (buttonElement) {
                buttonElement.classList.toggle('active', active);
            }
        }
        buttonContainerGet(id) {
            id = id || '';
            return this.buttonContainers.get(id);
        }
        /**
         * Add one to many buttonContainers to the buttonContainer map property by some id (blank id is fine for "main" one or if using a single container).
         * They should be added to rootElement at this time if not already done via template.
         * @returns The buttonContainer HTML Element
         * @protected
         */
        buttonContainerCreate() {
            const buttonContainer = document.createElement('div');
            buttonContainer.classList.add('button-container');
            this.buttonContainers.set('', buttonContainer); // default container id is blank so that components with only one button container dont have to worry about the id.
            this.rootElement.appendChild(buttonContainer);
        }
        buttonContainerEnableResizeObserver(id) {
            const element = this.buttonContainerGet(id);
            const resizeMgr = DIContainer.getInstance('ResizeManager');
            resizeMgr.observe(element, () => {
                this.buttonContainerHandleResize(id);
            });
            // resizeMgr.observe(element, this.buttonContainerHandleResize.bind(this));
            if (this.drawn) {
                this.renderButtons(id);
            }
        }
        buttonContainerDisableResizeObserver(id) {
            const element = this.buttonContainerGet(id);
            const resizeMgr = DIContainer.getInstance('ResizeManager');
            resizeMgr.unobserve(element);
            this.buttonContainerHandleResize.cancel();
        }
        draw() {
            super.draw();
            this.buttonContainerCreate();
            this.buttonContainers.forEach((element, id) => {
                if (element.classList.contains('overflowtype-menu') || element.classList.contains('overflowtype-scroll')) {
                    // register in the resize observable
                    this.buttonContainerEnableResizeObserver(id);
                }
            });
            if (this.buttons.length) {
                this.renderButtons();
            }
            // debounce to prevent double clicks running action twice, and to allow inputs to defocus and apply their value to the linked record.
            this.registerEvent('click', this.rootElement, (event) => {
                let target = event.target;
                // find which container they clicked, and then find which button they clicked. 
                this.buttonContainers.forEach((buttonContainer) => {
                    let buttonId;
                    if (!buttonId && buttonContainer.contains(event.target)) {
                        // If the target is an icon inside the button, adjust the target to be the button
                        while (target && target !== buttonContainer && !target.getAttribute('data-button-id')) {
                            target = target.parentElement;
                        }
                        buttonId = target.getAttribute('data-button-id');
                        if (buttonId) {
                            this.delegateClick(buttonId, event);
                        }
                    }
                });
            });
        }
        delegateClick(buttonId, event) {
            // need to force a blur on the focused component because of timing issues encountered where sometimes clicks run before blurs (even though the act of clicking should blur the input first)
            // and we need the blur to run first to ensure that if someone was in the middle of typing a value in an input, that the value is set in the linkedRecord before running our click. 
            DIContainer.getInstance('Application').blurFocusedComponent();
            this.debouncedButtonClick(buttonId, event);
        }
        renderButtons(id) {
            if (!this.drawn) {
                return;
            }
            if (typeof id === 'undefined') {
                this.buttonContainers.forEach((_buttonContainer, containerId) => {
                    this.renderButtons(containerId);
                });
                return;
            }
            const buttonContainer = this.buttonContainers.get(id);
            if (buttonContainer) {
                let scrollPosition = 0;
                if (buttonContainer.classList.contains('overflowtype-scroll')) {
                    scrollPosition = buttonContainer.querySelector('.button-scroll-container')?.scrollLeft || 0;
                }
                this.buttonContainerClear(id);
                this.buttons.sort((a, b) => {
                    return a.index - b.index;
                });
                // Create the overflow menu elements
                let overflowButton = null;
                let overflowButton2 = null;
                let overflowMenu = null;
                let overflowtype = 'menu';
                let internalButtonContainer = buttonContainer;
                if (buttonContainer.classList.contains('overflowtype-menu')) {
                    overflowButton = this.overflowButtonCreate(id, 'menu');
                    overflowMenu = document.querySelector(`.overflow-menu.${this.globalId}_${id}`);
                    buttonContainer.appendChild(overflowButton);
                }
                else if (buttonContainer.classList.contains('overflowtype-scroll')) {
                    overflowButton = this.overflowButtonCreate(id, 'scroll-l');
                    // buttonContainer.appendChild(overflowButton);
                    internalButtonContainer = createElement('span', ['button-scroll-container']);
                    buttonContainer.appendChild(internalButtonContainer);
                    overflowButton2 = this.overflowButtonCreate(id, 'scroll-r');
                    // buttonContainer.appendChild(overflowButton2);
                    overflowtype = 'scroll';
                }
                let totalButtonWidth = (overflowButton?.offsetWidth || 0) + 8;
                // let totalButtonWidth = overflowButton?.getBoundingClientRect().width || 0;
                const availableWidth = buttonContainer.clientWidth - 10; // Container width - 20 to allow for padding etc. not a fully robust solution
                totalButtonWidth += 8; // margin.. again not a fully robust solution
                this.buttons.forEach(button => {
                    if (button.containerId === id && button.id.indexOf(`${id}_overflow_`) === -1) {
                        const btnElement = this.renderButton(button);
                        if (btnElement /*&& !btnElement.classList.contains('hidden')*/) {
                            internalButtonContainer.appendChild(btnElement); // Add the button temporarily
                            const buttonWidth = btnElement.offsetWidth + 8; // margin.. again not a fully robust solution
                            button.renderedWidth = btnElement.classList.contains('hidden') ? 0 : buttonWidth;
                            totalButtonWidth += buttonWidth;
                            // If the total width exceeds the available space, move the button to the overflow menu
                            if (totalButtonWidth > availableWidth && overflowMenu) {
                                overflowMenu.appendChild(btnElement); // Move to overflow menu
                            }
                        }
                        else {
                            button.renderedWidth = 0;
                        }
                    }
                });
                // If overflow button exists and we moved some buttons, show the overflow button
                if (overflowButton && ((overflowMenu?.childElementCount || 0) > 0 || (internalButtonContainer.clientWidth - internalButtonContainer.scrollWidth) <= -10)) {
                    if (overflowtype === 'scroll') {
                        buttonContainer.insertBefore(overflowButton, buttonContainer.firstChild);
                        buttonContainer.appendChild(overflowButton2);
                    }
                    else {
                        buttonContainer.appendChild(overflowButton);
                        this.buttonGet(`${id}_overflow_menu`).renderedWidth = overflowButton.offsetWidth + 8;
                    }
                    if (scrollPosition) {
                        internalButtonContainer.scrollLeft = scrollPosition;
                    }
                }
                else if (overflowButton) {
                    overflowButton.remove();
                    // overflowButton2?.remove();
                    if (overflowtype === 'menu') {
                        this.buttonGet(`${id}_overflow_menu`).renderedWidth = 0;
                    }
                }
            }
        }
        buttonContainerClear(containerId) {
            const buttonContainer = this.buttonContainers.get(containerId);
            if (buttonContainer) {
                buttonContainer.innerHTML = '';
                if (buttonContainer.classList.contains('overflowtype-menu')) {
                    const overflowMenu = document.querySelector(`.overflow-menu.${this.globalId}_${containerId}`);
                    if (overflowMenu) {
                        overflowMenu.innerHTML = '';
                    }
                }
            }
        }
        overflowButtonCreate(containerId, type) {
            let button = this.buttonGet(`${containerId}_overflow_${type}`);
            if (!button) {
                if (type === 'menu') {
                    const overflowMenu = createElement('div', ['overflow-menu', 'button-container', `${this.globalId}_${containerId}`]);
                    if (this.formDesignerMode) {
                        overflowMenu.classList.add('form-designer');
                    }
                    document.body.appendChild(overflowMenu);
                    button = this.buttonDefaultsSet({
                        id: `${containerId}_overflow_menu`,
                        containerId,
                        classList: ['overflow-menu-button', 'container-style'],
                        icon: 'more_horiz',
                        index: 1000,
                        standardButton: true,
                        onClick: (event) => {
                            const overflowButton = event.target.closest('.overflow-menu-button');
                            overflowMenu.classList.toggle('active');
                            if (overflowMenu.classList.contains('active')) {
                                this.positionOverflowMenu(containerId, overflowButton);
                            }
                            else {
                                this.closeOverflowMenu(containerId);
                            }
                        }
                    });
                    this.registerEvent('click', overflowMenu, (event) => {
                        let target = event.target;
                        const buttonContainer = this.buttonContainerGet(containerId);
                        while (target && target !== buttonContainer && !target.getAttribute('data-button-id')) {
                            target = target.parentElement;
                        }
                        const buttonId = target.getAttribute('data-button-id');
                        if (buttonId) {
                            this.closeOverflowMenu(containerId);
                            this.delegateClick(buttonId, event);
                        }
                    });
                    this.registerEvent('mouseleave', overflowMenu, () => {
                        overflowMenu.classList.remove('hover');
                        if (!overflowMenu.classList.contains('active')) {
                            this.closeOverflowMenu(containerId);
                        }
                    });
                    this.registerEvent('mouseenter', overflowMenu, () => {
                        clearInterval(this.overflowCloseTimeout);
                    });
                }
                else {
                    button = this.buttonDefaultsSet({
                        id: `${containerId}_overflow_${type}`,
                        containerId,
                        classList: ['overflow-scroll-button', 'container-style', type],
                        icon: type === 'scroll-r' ? 'arrow_right' : 'arrow_left',
                        index: type === 'scroll-r' ? 1000 : -1000,
                        standardButton: true,
                        onClick: () => {
                            const buttonContainer = this.buttonContainerGet(containerId);
                            const internalButtonContainer = buttonContainer.querySelector('.button-scroll-container');
                            if (type === 'scroll-r') {
                                internalButtonContainer.scrollLeft += 40;
                            }
                            else {
                                internalButtonContainer.scrollLeft -= 40;
                            }
                        }
                    });
                }
                this.buttons.push(button);
            }
            if (this.overflowEventId1) {
                this.unRegisterEvent(this.overflowEventId1);
            }
            if (this.overflowEventId2) {
                this.unRegisterEvent(this.overflowEventId2);
            }
            const overflowButton = this.renderButton(button);
            if (type === 'menu') {
                const overflowMenu = document.querySelector(`.overflow-menu.${this.globalId}_${containerId}`);
                this.overflowEventId1 = this.registerEvent('mouseenter', overflowButton, () => {
                    if (!overflowMenu.classList.contains('hover') && !overflowMenu.classList.contains('active')) {
                        overflowMenu.classList.add('hover');
                        this.positionOverflowMenu(containerId, overflowButton);
                    }
                });
                this.overflowEventId2 = this.registerEvent('mouseleave', overflowButton, () => {
                    if (!overflowMenu.classList.contains('active')) {
                        this.overflowCloseTimeout = setTimeout(() => {
                            this.closeOverflowMenu(containerId);
                        }, 100);
                    }
                });
            }
            return overflowButton;
        }
        closeOverflowMenu(containerId) {
            const overflowmenu = document.querySelector(`.overflow-menu.${this.globalId}_${containerId}`);
            overflowmenu.classList.remove('hover');
            overflowmenu.classList.remove('active');
        }
        positionOverflowMenu(containerId, overflowButton) {
            const overflowmenu = document.querySelector(`.overflow-menu.${this.globalId}_${containerId}`);
            const buttonRect = overflowButton.getBoundingClientRect();
            const menuHeight = overflowmenu.offsetHeight;
            const menuWidth = overflowmenu.offsetWidth;
            const availableHeight = window.innerHeight;
            const availableWidth = window.innerWidth;
            // Determine whether the menu should open upwards or downwards
            const shouldOpenUpwards = buttonRect.bottom + menuHeight > availableHeight && buttonRect.top - menuHeight >= 0;
            const shouldOpenLeft = buttonRect.left + menuWidth > availableWidth && buttonRect.right - menuWidth >= 0;
            // Reset the style
            overflowmenu.removeAttribute('style');
            // Calculate the Y position (above or below the button)
            if (shouldOpenUpwards) {
                // Open upwards
                overflowmenu.style.top = `${buttonRect.top - menuHeight}px`;
            }
            else {
                // Open downwards
                overflowmenu.style.top = `${buttonRect.bottom}px`;
            }
            // Calculate the X position (left or right alignment with respect to the button)
            if (shouldOpenLeft) {
                // Align to the left of the button
                overflowmenu.style.left = `${buttonRect.right - menuWidth}px`;
            }
            else {
                // Align to the right of the button
                overflowmenu.style.left = `${buttonRect.left}px`;
            }
            if (this._buttonClickAwayEventId) {
                this.unRegisterEvent(this._buttonClickAwayEventId);
            }
            debounce(() => {
                this._buttonClickAwayEventId = this.registerEvent('click', document, () => {
                    this.closeOverflowMenu(containerId);
                    this.unRegisterEvent(this._buttonClickAwayEventId);
                });
            }, 1)();
        }
        shouldButtonDisable(button) {
            return button.disabled
                || (button.syncComponentDisabledState && this._disabledState)
                || !!(button.enabledRecordStates && button.enabledRecordStates.length && button.enabledRecordStates.indexOf(this.recordState) === -1);
        }
        renderButton(button) {
            if (button.viewCondition && !DIContainer.getInstance('ConditionsManager').validateConditions(button.viewCondition, {
                initiator: this,
                record: this.record
            })) {
                return null;
            }
            const btnElement = document.createElement('button');
            btnElement.setAttribute('data-button-id', button.id);
            let included = true;
            if (button.includeProperty) {
                included = this.propertyGet(button.includeProperty);
            }
            if (button.hidden || !included) {
                btnElement.classList.add('hidden');
            }
            if (button.disabledState) {
                btnElement.classList.add('disabled');
                // in the form designer, we need to allow the disabled tab to be selected so it can be designed. 
                if (!this.formDesignerMode || this.componentName !== 'TabSet') {
                    btnElement.setAttribute('disabled', 'disabled');
                }
            }
            // if (button.invertColors) {
            // 	btnElement.classList.add('invert-colors');
            // }
            if (button.hover) {
                if (typeof button.hover === 'string')
                    button.hover = [button.hover];
                button.hover.forEach((animation) => {
                    btnElement.classList.add(`hover-${animation}`);
                });
            }
            if (button.animate) {
                if (typeof button.animate === 'string')
                    button.animate = [button.animate];
                button.animate.forEach((animation) => {
                    btnElement.classList.add(`animate-${animation}`);
                });
            }
            if (button.icon) {
                const iconElement = this.IconManager.getIconElement('');
                iconElement.classList.add('empty');
                if (button.icon instanceof Observable) {
                    if (button.iconSubscription) {
                        button.iconSubscription.unsubscribe();
                    }
                    button.iconSubscription = button.icon
                        .subscribe((icon) => {
                        this.IconManager.updateIcon(iconElement, icon);
                    });
                }
                else {
                    this.IconManager.updateIcon(iconElement, button.icon);
                }
                btnElement.appendChild(iconElement);
            }
            if (button.captionKey) {
                button.caption = this.CaptionManager.getCaption(`${button.captionKey}_caption`) || button.caption; // database caption trumps hardcoded captions
                button.tooltip = this.CaptionManager.getCaption(`${button.captionKey}_tooltip`) || button.tooltip;
            }
            if (button.tooltip) {
                const opts = {
                    ...{
                        tooltip: button.tooltip,
                        delay: 800,
                        clickAway: true,
                        sidePriority: ['top', 'bottom', 'left', 'right']
                    },
                    ...(this.defaultTooltipOptions || {}),
                    ...(button.tooltipOptions || {})
                };
                this.registerTooltip(btnElement, opts);
            }
            if (button.caption) {
                // Create a text node for the caption
                const textNode = document.createElement('span');
                textNode.classList.add('button-text');
                if (button.caption instanceof Observable) {
                    if (button.captionSubscription) {
                        button.captionSubscription.unsubscribe();
                    }
                    btnElement.appendChild(textNode);
                    button.captionSubscription = button.caption
                        .subscribe((caption) => {
                        textNode.innerText = caption;
                        let buttonWidth = btnElement.offsetWidth + 8;
                        buttonWidth = btnElement.classList.contains('hidden') ? 0 : buttonWidth;
                        if (buttonWidth !== button.renderedWidth) {
                            button.renderedWidth = buttonWidth;
                            this.buttonContainerHandleResize(button.containerId || '');
                        }
                    });
                }
                else {
                    textNode.innerText = button.caption;
                    btnElement.appendChild(textNode);
                }
            }
            if (button.active) {
                btnElement.classList.add('active');
            }
            if (button.classList) {
                button.classList.forEach((btnClass) => btnElement.classList.toggle(btnClass, true));
            }
            return btnElement;
        }
        refreshButton(id) {
            if (!this.drawn) {
                return;
            }
            if (!id) {
                this.renderButtons();
            }
            else {
                // TODO: actually implement functionality for single buttons
                this.renderButtons();
            }
        }
        handleRecordStateChange(state) {
            super.handleRecordStateChange(state);
            if (!this._enabledRecordStates) {
                // the component doesnt have enabledRecordStates, so it wont call updateDisabledState in the super code. But our buttons might have enabledRecordStates. 
                this.updateDisabledState();
            }
        }
        updateDisabledState() {
            super.updateDisabledState();
            let render = false;
            this.buttons?.forEach((button) => {
                if (button.disabledState !== this.shouldButtonDisable(button)) {
                    button.disabledState = !button.disabledState;
                    render = true;
                }
            });
            if (render) {
                this.renderButtons();
            }
        }
        formDesignerPropertiesGet(properties) {
            properties = super.formDesignerPropertiesGet(properties);
            const customButtons = [];
            let customizedButtons = [];
            const standardButtons = [];
            this.buttons.forEach((button) => {
                if (button.includeProperty) {
                    properties[button.includeProperty] = ({
                        id: button.includeProperty,
                        value: this.propertyGet(button.includeProperty),
                        componentType: 'InputCheckbox',
                        componentProperties: {
                            checkedValue: true,
                            unCheckedValue: false,
                            hidden: true // now visible in the grid but we still need to save the property
                        },
                        captionKey: `formbuilder_buttons_${button.includeProperty.toLowerCase()}`,
                        propertyGroup: 'buttons',
                        propertyIndex: 1,
                        saveFlag: () => button.defaultIncludePropertyValue != this.propertyGet(button.includeProperty)
                    });
                    standardButtons.push({
                        id: button.id,
                        viewCondition: button.viewCondition,
                        enabledRecordStates: button.enabledRecordStates,
                        include: this[button.includeProperty],
                        index: button.index
                    });
                    const customizations = {
                        id: button.id
                    };
                    let found = false;
                    if (button.formBuilderDefaultValues && button.formBuilderDefaultValues.viewCondition !== button.viewCondition) {
                        found = true;
                        customizations.viewCondition = button.viewCondition;
                    }
                    if (button.formBuilderDefaultValues && JSON.stringify(button.formBuilderDefaultValues.enabledRecordStates) !== JSON.stringify(button.enabledRecordStates)) {
                        found = true;
                        customizations.enabledRecordStates = button.enabledRecordStates;
                    }
                    if (found) {
                        customizedButtons.push(customizations);
                    }
                }
                else if (!button.standardButton) {
                    customButtons.push({
                        id: button.id,
                        icon: button.icon,
                        eventConfigName: button.eventConfigName,
                        viewCondition: button.viewCondition,
                        enabledRecordStates: button.enabledRecordStates,
                        index: button.index,
                        captionKey: button.captionKey
                    });
                    if (button.captionKey) {
                        properties.dependentCaptionKeys.value.push(button.captionKey);
                    }
                }
            });
            customizedButtons = [...customizedButtons, ...customButtons];
            if (standardButtons.length) {
                properties.buttonExtensions = {
                    id: 'buttonExtensions',
                    value: standardButtons,
                    componentType: 'InputGrid',
                    componentProperties: {
                        dataFormat: 'ARRAY',
                        dbGridName: 'Q360FormComponentStandardButtons',
                        labelPosition: 'top',
                        grid: {
                            autoResize: false
                        }
                    },
                    captionKey: 'formbuilder_buttons_standard',
                    propertyGroup: 'buttons',
                    propertyIndex: 98,
                    handlePropertyChange: (buttons) => {
                        buttons.forEach((button) => {
                            const standardButton = this.buttonGet(button.id);
                            if (standardButton) {
                                standardButton.viewCondition = button.viewCondition;
                                standardButton.enabledRecordStates = button.enabledRecordStates;
                                this[standardButton.includeProperty] = button.include;
                            }
                        });
                        this.updateDisabledState();
                        this.renderButtons();
                        return buttons;
                    },
                    saveFlag: false
                };
            }
            properties.buttons = {
                id: 'buttons',
                value: customButtons,
                componentType: 'InputGrid',
                componentProperties: {
                    dataFormat: 'ARRAY',
                    dbGridName: 'Q360FormComponentButtons',
                    includeAddButton: true,
                    includeDeleteButton: true,
                    labelPosition: 'top',
                    grid: {
                        autoResize: false
                    }
                },
                captionKey: 'formbuilder_buttons_custom',
                propertyGroup: 'buttons',
                propertyIndex: 99 // last
                ,
                saveFlag: !!customizedButtons.length,
                saveValue: customizedButtons
            };
            return properties;
        }
        destroy() {
            this.buttonContainers.forEach((element, id) => {
                if (element.classList.contains('overflowtype-menu') || element.classList.contains('overflowtype-scroll')) {
                    this.buttonContainerDisableResizeObserver(id);
                    const overflowMenu = document.querySelector(`.overflow-menu.${this.globalId}_${id}`);
                    overflowMenu?.remove();
                }
            });
            this.buttons.length = 0;
            this.buttonContainers.clear();
            super.destroy();
        }
    };
}
